// src/utils/colors.js
// Comprehensive color system utilities for Hooptie UI

/**
 * Color System Constants
 * These match the CSS custom properties defined in colors.css
 */
export const COLORS = {
  // Primary Colors
  PRIMARY: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6', // Main Primary
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
    950: '#172554',
  },

  // Secondary Colors
  SECONDARY: {
    50: '#faf5ff',
    100: '#f3e8ff',
    200: '#e9d5ff',
    300: '#d8b4fe',
    400: '#c084fc',
    500: '#a855f7', // Main Secondary
    600: '#9333ea',
    700: '#7c3aed',
    800: '#6b21a8',
    900: '#581c87',
    950: '#3b0764',
  },

  // Accent Colors
  ACCENT: {
    50: '#f0fdfa',
    100: '#ccfbf1',
    200: '#99f6e4',
    300: '#5eead4',
    400: '#2dd4bf',
    500: '#14b8a6', // Main Accent
    600: '#0d9488',
    700: '#0f766e',
    800: '#115e59',
    900: '#134e4a',
    950: '#042f2e',
  },

  // Neutral Colors
  GRAY: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
    950: '#030712',
  },

  // Dark Theme Colors
  DARK: {
    50: '#f8fafc',
    100: '#f1f5f9',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
    950: '#020617',
  },

  // Semantic Colors
  SUCCESS: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e', // Main Success
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
    950: '#052e16',
  },

  WARNING: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b', // Main Warning
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
    950: '#451a03',
  },

  ERROR: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444', // Main Error
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
    950: '#450a0a',
  },

  INFO: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6', // Main Info
    600: '#2563eb',
    700: '#1d4ed8',
    800: '#1e40af',
    900: '#1e3a8a',
    950: '#172554',
  },
};

/**
 * Theme Colors
 */
export const THEME_COLORS = {
  // Dark Theme
  DARK: {
    bg: {
      primary: '#0d0f1a',
      secondary: '#141726',
      tertiary: '#1a1d2d',
      elevated: '#222736',
      overlay: '#0b0e17',
    },
    text: {
      primary: '#ffffff',
      secondary: '#e4e6eb',
      tertiary: '#a0a3b1',
      muted: '#8c93a3',
      disabled: '#6b7280',
    },
    border: {
      primary: 'rgba(255, 255, 255, 0.08)',
      secondary: 'rgba(255, 255, 255, 0.06)',
      tertiary: 'rgba(255, 255, 255, 0.04)',
      accent: 'rgba(147, 51, 234, 0.12)',
    },
  },

  // Light Theme
  LIGHT: {
    bg: {
      primary: '#ffffff',
      secondary: '#f8fafc',
      tertiary: '#f1f5f9',
      elevated: '#ffffff',
      overlay: '#f8fafc',
    },
    text: {
      primary: '#0f172a',
      secondary: '#334155',
      tertiary: '#64748b',
      muted: '#94a3b8',
      disabled: '#cbd5e1',
    },
    border: {
      primary: 'rgba(0, 0, 0, 0.1)',
      secondary: 'rgba(0, 0, 0, 0.08)',
      tertiary: 'rgba(0, 0, 0, 0.06)',
      accent: 'rgba(59, 130, 246, 0.2)',
    },
  },
};

/**
 * Gradient Definitions
 */
export const GRADIENTS = {
  PRIMARY: 'linear-gradient(90deg, #3b82f6, #a855f7)',
  PRIMARY_VERTICAL: 'linear-gradient(180deg, #3b82f6, #a855f7)',
  PRIMARY_RADIAL: 'radial-gradient(circle, #3b82f6, #a855f7)',
  
  SECONDARY: 'linear-gradient(90deg, #a855f7, #14b8a6)',
  SECONDARY_VERTICAL: 'linear-gradient(180deg, #a855f7, #14b8a6)',
  
  ACCENT: 'linear-gradient(90deg, #14b8a6, #3b82f6)',
  ACCENT_VERTICAL: 'linear-gradient(180deg, #14b8a6, #3b82f6)',
  
  DARK: {
    PRIMARY: 'linear-gradient(180deg, #101322, #0d0f1a)',
    SECONDARY: 'linear-gradient(180deg, #141726, #0d0f1a)',
    ACCENT: 'linear-gradient(180deg, #1a1d2d, #141726)',
  },
};

/**
 * Shadow Definitions
 */
export const SHADOWS = {
  // Light Shadows
  XS: '0 1px 2px rgba(0, 0, 0, 0.05)',
  SM: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
  MD: '0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06)',
  LG: '0 10px 15px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)',
  XL: '0 20px 25px rgba(0, 0, 0, 0.1), 0 10px 10px rgba(0, 0, 0, 0.04)',
  '2XL': '0 25px 50px rgba(0, 0, 0, 0.25)',

  // Dark Shadows
  DARK: {
    XS: '0 1px 2px rgba(0, 0, 0, 0.3)',
    SM: '0 1px 3px rgba(0, 0, 0, 0.4), 0 1px 2px rgba(0, 0, 0, 0.3)',
    MD: '0 4px 6px rgba(0, 0, 0, 0.5), 0 2px 4px rgba(0, 0, 0, 0.4)',
    LG: '0 10px 15px rgba(0, 0, 0, 0.6), 0 4px 6px rgba(0, 0, 0, 0.5)',
    XL: '0 20px 25px rgba(0, 0, 0, 0.7), 0 10px 10px rgba(0, 0, 0, 0.6)',
    '2XL': '0 25px 50px rgba(0, 0, 0, 0.8)',
  },

  // Colored Shadows
  PRIMARY: '0 0 20px rgba(59, 130, 246, 0.3)',
  SECONDARY: '0 0 20px rgba(168, 85, 247, 0.3)',
  ACCENT: '0 0 20px rgba(20, 184, 166, 0.3)',
  SUCCESS: '0 0 20px rgba(34, 197, 94, 0.3)',
  WARNING: '0 0 20px rgba(245, 158, 11, 0.3)',
  ERROR: '0 0 20px rgba(239, 68, 68, 0.3)',
};

/**
 * Component Color Schemes
 */
export const COMPONENT_COLORS = {
  // Button Colors
  BUTTON: {
    PRIMARY: {
      bg: GRADIENTS.PRIMARY,
      hover: GRADIENTS.PRIMARY_VERTICAL,
      text: '#ffffff',
      border: 'transparent',
    },
    SECONDARY: {
      bg: THEME_COLORS.DARK.bg.secondary,
      hover: THEME_COLORS.DARK.bg.tertiary,
      text: THEME_COLORS.DARK.text.primary,
      border: THEME_COLORS.DARK.border.primary,
    },
    SUCCESS: {
      bg: COLORS.SUCCESS[500],
      hover: COLORS.SUCCESS[600],
      text: '#ffffff',
      border: 'transparent',
    },
    WARNING: {
      bg: COLORS.WARNING[500],
      hover: COLORS.WARNING[600],
      text: '#ffffff',
      border: 'transparent',
    },
    ERROR: {
      bg: COLORS.ERROR[500],
      hover: COLORS.ERROR[600],
      text: '#ffffff',
      border: 'transparent',
    },
  },

  // Input Colors
  INPUT: {
    bg: THEME_COLORS.DARK.bg.tertiary,
    border: THEME_COLORS.DARK.border.primary,
    borderFocus: COLORS.PRIMARY[500],
    text: THEME_COLORS.DARK.text.primary,
    placeholder: THEME_COLORS.DARK.text.muted,
  },

  // Card Colors
  CARD: {
    bg: THEME_COLORS.DARK.bg.secondary,
    border: THEME_COLORS.DARK.border.secondary,
    shadow: SHADOWS.DARK.MD,
  },

  // Table Colors
  TABLE: {
    header: THEME_COLORS.DARK.bg.tertiary,
    row: THEME_COLORS.DARK.bg.secondary,
    rowHover: 'rgba(59, 130, 246, 0.15)',
    border: THEME_COLORS.DARK.border.tertiary,
  },

  // Modal Colors
  MODAL: {
    bg: THEME_COLORS.DARK.bg.secondary,
    overlay: 'rgba(0, 0, 0, 0.7)',
    border: THEME_COLORS.DARK.border.secondary,
  },
};

/**
 * Utility Functions
 */

/**
 * Get a color with opacity
 * @param {string} color - Hex color
 * @param {number} opacity - Opacity value (0-1)
 * @returns {string} RGBA color string
 */
export const withOpacity = (color, opacity) => {
  // Convert hex to RGB
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Get a color from the palette
 * @param {string} category - Color category (PRIMARY, SECONDARY, etc.)
 * @param {number|string} shade - Shade number (50, 100, 500, etc.)
 * @returns {string} Hex color
 */
export const getColor = (category, shade) => {
  return COLORS[category.toUpperCase()]?.[shade] || COLORS.PRIMARY[500];
};

/**
 * Get theme color
 * @param {string} theme - Theme name (DARK, LIGHT)
 * @param {string} category - Color category (bg, text, border)
 * @param {string} variant - Color variant (primary, secondary, etc.)
 * @returns {string} Color value
 */
export const getThemeColor = (theme, category, variant) => {
  return THEME_COLORS[theme.toUpperCase()]?.[category]?.[variant] || THEME_COLORS.DARK.bg.primary;
};

/**
 * Get component color
 * @param {string} component - Component name (BUTTON, INPUT, etc.)
 * @param {string} variant - Component variant (PRIMARY, SECONDARY, etc.)
 * @param {string} property - Color property (bg, text, border, etc.)
 * @returns {string} Color value
 */
export const getComponentColor = (component, variant, property) => {
  return COMPONENT_COLORS[component.toUpperCase()]?.[variant.toUpperCase()]?.[property] || '';
};

/**
 * Generate a color palette with different shades
 * @param {string} baseColor - Base hex color
 * @param {number} steps - Number of shades to generate
 * @returns {object} Object with shade numbers as keys
 */
export const generateColorPalette = (baseColor, steps = 10) => {
  const palette = {};
  
  for (let i = 0; i < steps; i++) {
    const shade = i * 100;
    const opacity = 1 - (i / steps);
    palette[shade] = withOpacity(baseColor, opacity);
  }
  
  return palette;
};

/**
 * Check if a color is light or dark
 * @param {string} color - Hex color
 * @returns {string} 'light' or 'dark'
 */
export const getColorContrast = (color) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  return luminance > 0.5 ? 'light' : 'dark';
};

/**
 * Get appropriate text color for a background
 * @param {string} bgColor - Background hex color
 * @returns {string} Text color (light or dark)
 */
export const getTextColorForBackground = (bgColor) => {
  const contrast = getColorContrast(bgColor);
  return contrast === 'light' ? COLORS.GRAY[900] : '#ffffff';
};

// Export default colors object for easy access
export default {
  COLORS,
  THEME_COLORS,
  GRADIENTS,
  SHADOWS,
  COMPONENT_COLORS,
  withOpacity,
  getColor,
  getThemeColor,
  getComponentColor,
  generateColorPalette,
  getColorContrast,
  getTextColorForBackground,
};
